import { Minus, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useCallback } from 'react'

interface QuantitySelectorProps {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  size?: 'sm' | 'md'
  className?: string
}

export function QuantitySelector({
  value,
  onChange,
  min = 1,
  max = 99,
  size = 'md',
  className,
}: QuantitySelectorProps) {
  const clamp = useCallback(
    (n: number) => Math.max(min, Math.min(max, Math.round(n))),
    [min, max]
  )

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1 rounded-lg border border-zinc-200',
        size === 'sm' ? 'h-8' : 'h-10',
        className
      )}
    >
      <Button
        type="button"
        variant="ghost"
        size={size === 'sm' ? 'icon-xs' : 'icon-sm'}
        className="text-zinc-500 hover:text-zinc-900"
        onClick={() => onChange(clamp(value - 1))}
        disabled={value <= min}
        aria-label="Decrease quantity"
      >
        <Minus className={size === 'sm' ? 'size-3' : 'size-4'} />
      </Button>

      <input
        type="number"
        inputMode="numeric"
        value={value}
        onChange={e => {
          const n = parseInt(e.target.value, 10)
          if (!isNaN(n)) onChange(clamp(n))
        }}
        onBlur={() => onChange(clamp(value))}
        min={min}
        max={max}
        className={cn(
          'w-10 bg-transparent text-center text-sm font-medium text-zinc-900 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
          size === 'sm' && 'w-8 text-xs'
        )}
        aria-label="Quantity"
      />

      <Button
        type="button"
        variant="ghost"
        size={size === 'sm' ? 'icon-xs' : 'icon-sm'}
        className="text-zinc-500 hover:text-zinc-900"
        onClick={() => onChange(clamp(value + 1))}
        disabled={value >= max}
        aria-label="Increase quantity"
      >
        <Plus className={size === 'sm' ? 'size-3' : 'size-4'} />
      </Button>
    </div>
  )
}
